import { Brain } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import type { ISSEQueryAgentPayload } from 'features/chat/agent-chat-module/types/agent.service.type';
import { AnimatedDots } from 'features/chat/agent-chat-module/utils/chat-event-messages';
import { parseSSEBuffer } from 'features/chat/agent-chat-module/utils/parse-sse';
import { useQuerySSE } from 'features/chat/hooks/use-agents';
import { getApiErrorDetail } from 'utils/api-error';
import { buildBasePromptGenerationPrompt } from './base-prompt-generation-prompt';

type BasePromptAiModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUse: (basePrompt: string) => void;
  agentName?: string;
  basePromptValue: string;
  toolIds?: string[];
};

export function BasePromptAiModal({
  open,
  onOpenChange,
  onUse,
  agentName,
  basePromptValue,
  toolIds,
}: BasePromptAiModalProps) {
  const { streamQuery } = useQuerySSE();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedBasePrompt, setGeneratedBasePrompt] = useState('');
  const statusMessage = 'Generating';

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setGeneratedBasePrompt('');
    setIsGenerating(false);
  }, [onOpenChange]);

  const generateBasePrompt = useCallback(async () => {
    try {
      setIsGenerating(true);
      setGeneratedBasePrompt('');

      const resolvedAgentName = agentName || 'Assistant';
      const payload: ISSEQueryAgentPayload = {
        query:
          basePromptValue?.trim() ||
          `Create a strong base prompt for an AI agent named ${resolvedAgentName}.`,
        base_prompt: buildBasePromptGenerationPrompt(resolvedAgentName),
        last_n_turns: 5,
        enable_summary: true,
        enable_tools: true,
        enable_next_suggestion: false,
        call_from: 'blocks',
        tool_ids: toolIds,
      };

      const reader = await streamQuery(payload);
      const decoder = new TextDecoder();
      let buffer = '';
      let isDone = false;

      while (!isDone) {
        const { done, value } = await reader.read();
        isDone = done;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = parseSSEBuffer(buffer);
        buffer = remaining;

        events.forEach(({ eventType, eventData }) => {
          switch (eventType) {
            case 'chat_response':
            case 'final_answer': {
              const content = (eventData.result || eventData.message) as string;
              if (content) {
                setGeneratedBasePrompt(content);
                setIsGenerating(false);
              }
              break;
            }
            case 'error':
              setGeneratedBasePrompt(
                'An error occurred while generating the base prompt.'
              );
              setIsGenerating(false);
              break;
            default:
              break;
          }
        });
      }

      setIsGenerating(false);
    } catch (error) {
      setGeneratedBasePrompt(
        getApiErrorDetail(error) ?? 'Failed to generate base prompt. Please try again.',
      );
      setIsGenerating(false);
    }
  }, [agentName, basePromptValue, streamQuery, toolIds]);

  useEffect(() => {
    if (open && generatedBasePrompt === '' && !isGenerating) {
      void generateBasePrompt();
    }
  }, [generateBasePrompt, generatedBasePrompt, isGenerating, open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Base Prompt</DialogTitle>
        </DialogHeader>

        <div className="pb-4">
          {isGenerating ? (
            <div className="flex min-h-[120px] items-center justify-center rounded-lg border bg-card">
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
                  <Brain className="h-6 w-6 animate-pulse text-white" />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <span className="text-sm text-muted-foreground">{statusMessage}</span>
                  <AnimatedDots />
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Textarea
                value={generatedBasePrompt}
                onChange={(event) => setGeneratedBasePrompt(event.target.value)}
                placeholder="Add the agent purpose or rough instructions, then use AI to turn them into a polished base prompt."
                rows={12}
                className="resize-none border bg-card"
              />
              <div className="mt-2 text-right text-sm text-muted-foreground">
                {generatedBasePrompt.length} characters
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void generateBasePrompt()}
            disabled={isGenerating}
            className="mr-auto pl-0 text-primary hover:bg-transparent"
          >
            Regenerate
          </Button>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onUse(generatedBasePrompt);
              handleClose();
            }}
            disabled={isGenerating || !generatedBasePrompt.trim()}
          >
            Use
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
